import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@bindery/ui/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@bindery/ui/components/ui/tooltip";
import { cn } from "@bindery/ui/lib/utils";
import { HelpCircle } from "lucide-react";

interface FluencyProgressProps {
  wordsByFluency: {
    unknown: number;
    initial: number;
    developing: number;
    learned: number;
    strong: number;
  };
  totalWords: number;
}

const FLUENCY_LEVEL_CONFIG = [
  {
    name: "Strong",
    key: "strong" as const,
    description: "Consistent recognition",
    color: "bg-mintleaf-600",
    textColor: "text-mintleaf-700",
  },
  {
    name: "Learned",
    key: "learned" as const,
    description: "Consecutive recognition",
    color: "bg-skyfall-500",
    textColor: "text-skyfall-700",
  },
  {
    name: "Developing",
    key: "developing" as const,
    description: "Developing recognition, not yet consistent",
    color: "bg-flamenco-400",
    textColor: "text-flamenco-700",
  },
  {
    name: "Initial",
    key: "initial" as const,
    description: "Recognition on first encounter, waiting on more data",
    color: "bg-plumwood-400",
    textColor: "text-plumwood-700",
  },
];

export function FluencyProgress({
  wordsByFluency,
  totalWords,
}: FluencyProgressProps) {
  return (
    <TooltipProvider>
      <Card>
        <CardHeader>
          <CardTitle>Fluency Progress</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {FLUENCY_LEVEL_CONFIG.map((level) => {
            const count = wordsByFluency[level.key];
            const percentage = totalWords > 0 ? (count / totalWords) * 100 : 0;
            return (
              <div key={level.key} className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        className={cn(
                          "inline-flex cursor-help items-center gap-1 border-none bg-transparent p-0 font-medium",
                          level.textColor,
                        )}
                      >
                        {level.name}
                        <HelpCircle
                          className="size-3.5 opacity-60"
                          aria-hidden="true"
                        />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{level.description}</p>
                    </TooltipContent>
                  </Tooltip>
                  <span className="text-muted-foreground">
                    {percentage.toFixed(0)}% ({count} words)
                  </span>
                </div>
                {/* Custom progress bar with colored indicator */}
                <div className="bg-primary/20 relative h-2 w-full overflow-hidden rounded-full">
                  <div
                    className={cn("h-full transition-all", level.color)}
                    style={{ width: `${percentage}%` }}
                  />
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>
    </TooltipProvider>
  );
}
